import React, { useEffect, useMemo } from "react";
import { createStackNavigator } from "@react-navigation/stack";
import { TouchableOpacity, Text, Platform, Image, Linking, View } from "react-native";
import ListView from "../../views/Events/ListView/ListView";
import FiltersView from "../../views/Events/FilterView/FiltersView";
import DetailsView from "../../views/Events/DetailsView/DetailsView";
import ImageGalleryModal from "../ImageGalleryModal/ImageGalleryModal";
import ParkingDetailModal from "../ParkingDetailModal/ParkingDetailModal";
import CalendarModal from "../CalendarModal/CalendarModal";
import ParkingStore from "../../storage/parkingStore";
import EventStore from "../../storage/eventStore";
import { STRINGS, DATE_FORMAT } from "../../constants";
import { HeaderBackButton } from "@react-navigation/elements";

// Create the stack navigator instance
const Stack = createStackNavigator();

/**
 * EventsStack Component
 *
 * Manages the navigation stack for the Events feature.
 * Includes:
 * - ListView: Displays a list of events.
 * - FiltersView: Provides event filtering options.
 * - DetailsView: Displays event details.
 * - ParkingModal: Shows parking details in a modal.
 *
 * Headers are dynamically managed to optimize layout for both mobile and web.
 *
 * @returns {JSX.Element} The rendered EventsStack component.
 */
const EventsStack = () => {
  const fetchParking = ParkingStore((state) => state.fetchParking);
  const selectedFromDate = EventStore((state) => state.selectedFromDate);
  const selectedToDate = EventStore((state) => state.selectedToDate);
  const totalFound = EventStore((state) => state.totalFound);
  const initialLoading = EventStore((state) => state.initialLoading);
  const fetchFeaturedEvents = EventStore((state) => state.fetchFeaturedEvents);

  // Fetch parking data and featured events once on mount
  useEffect(() => {
    fetchParking();
    fetchFeaturedEvents();
  }, []);

  // Format the selected date safely using constants
  const formattedDate = useMemo(() => {
    const from = new Date(selectedFromDate);
    const to = new Date(selectedToDate);

    if (!selectedFromDate || !selectedToDate || isNaN(from.getTime()) || isNaN(to.getTime())) {
      return `${STRINGS.all}`;
    }

    const isSameDay = from.toDateString() === to.toDateString();

    return isSameDay
      ? `${from.toLocaleDateString("en-US", DATE_FORMAT)}`
      : `${from.toLocaleDateString("en-US", DATE_FORMAT)} - ${to.toLocaleDateString("en-US", DATE_FORMAT)}`;
  }, [selectedFromDate, selectedToDate]);

  const DownloadButtonTemplate = ({ url, src }) => {
    return (
      <TouchableOpacity onPress={() => Linking.openURL(url)} style={{ marginRight: 16 }}>
        <Image source={src} style={{ width: 24, height: 24 }} resizeMode="contain" />
      </TouchableOpacity>
    );
  };

  const GoogleDownloadButton = () => {
    return (
      <DownloadButtonTemplate
        url={STRINGS.googlePlaystoreLink}
        src={require("../../assets/images/google-play-icon.png")}
      />
    );
  };

  const AppleDownloadButton = () => {
    return (
      <DownloadButtonTemplate url={STRINGS.appleAppstoreLink} src={require("../../assets/images/app-store-icon.png")} />
    );
  };

  const DownloadButton = () => {
    if (/Android/i.test(navigator.userAgent)) {
      return <GoogleDownloadButton />;
    }
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      return <AppleDownloadButton />;
    }
    if (
      Platform.OS === "web" &&
      /Android/i.test(navigator.userAgent) === false &&
      /iPhone|iPad|iPod/i.test(navigator.userAgent) === false
    ) {
      return (
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <AppleDownloadButton />
          <GoogleDownloadButton />
        </View>
      );
    }
  };

  return (
    <Stack.Navigator initialRouteName="ListView">
      {/* ListView Screen - Displays events */}
      <Stack.Screen
        name="ListView"
        component={ListView}
        options={{
          title: STRINGS.listViewTitle
            .replace("{formattedDate}", formattedDate)
            .replace("{eventsTitle}", STRINGS.eventsTitle)
            .replace(
              "{totalFound}",
              !initialLoading && totalFound
                ? totalFound.toLocaleString("en-US")
                : !initialLoading && totalFound === 0
                ? STRINGS.no
                : STRINGS.loading
            ),
          headerRight: () => <DownloadButton />,
        }}
      />

      {/* FiltersView Screen - Provides filtering options */}
      <Stack.Screen
        name="FiltersView"
        component={FiltersView}
        options={({ navigation }) => ({
          title: STRINGS.filterViewTitle,
          headerBackTitle: STRINGS.back,
          headerRight: () => (
            <TouchableOpacity onPress={() => navigation.setParams({ clearFilters: true })} style={{ marginRight: 16 }}>
              <Text style={{ color: "#007AFF", fontSize: 16 }}>{STRINGS.clearAll}</Text>
            </TouchableOpacity>
          ),
        })}
      />

      {/* DetailsView Screen - Shows event details */}
      <Stack.Screen
        name="DetailsView"
        component={DetailsView}
        options={({ navigation }) => {
          const canGoBack = navigation.canGoBack();
          const hideNavigationBar = Platform.OS === "android" && !canGoBack ? { headerShown: false } : {};

          return {
            ...hideNavigationBar,
            headerBackTitle: STRINGS.back,
            // Only override the back button behavior if there's no history on web
            ...(Platform.OS === "web" && !canGoBack
              ? {
                  headerLeft: () => (
                    <HeaderBackButton
                      tintColor="#000"
                      onPress={() => {
                        window.location.href = window.location.origin;
                      }}
                    />
                  ),
                }
              : {}),
          };
        }}
      />

      {/* Parking Modal Screen */}
      <Stack.Screen
        name="ParkingModal"
        component={ParkingDetailModal}
        options={{ presentation: "modal", headerShown: false }}
      />
      <Stack.Screen
        name="ImageGalleryModal"
        component={ImageGalleryModal}
        options={{ presentation: "modal", headerShown: false }}
      />
      <Stack.Screen
        name="CalendarModal"
        component={CalendarModal}
        options={{ presentation: "modal", headerShown: false }}
      />
    </Stack.Navigator>
  );
};

export default EventsStack;
